/**
 * Contact / RFQ form for the AnyAngle marketing site.
 *
 * Submission flow (two-step):
 *   1. For each attached file: POST /presign → PUT directly to S3 (with
 *      progress) → collect the returned S3 key.
 *   2. POST /submit with { name, email, ..., fileKeys, turnstileToken }.
 *
 * WHY two steps: presigned URLs let us bypass Lambda's 6 MB payload limit
 * and avoid paying for compute time while big CAD files transit. The Lambda
 * only ever sees the small JSON metadata.
 *
 * Bot protection in this component:
 *   - Honeypot input hidden with CSS (NOT type="hidden" — bots fill those).
 *   - Cloudflare Turnstile invisible widget producing a token the server
 *     re-verifies via /siteverify.
 *
 * Styling: per-element class strings live in the STYLES table below, keyed by
 * `variant`. The "light" row is the original palette (slate-100 button on
 * slate-900 inputs); "dark" matches the marketing-site dark card aesthetic
 * (brand-blue button on near-black inputs). Every JSX node, hook, ref, and
 * handler in the form is variant-agnostic — only class strings change.
 */
import { useEffect, useRef, useState } from "react";
import {
  ALLOWED_EXTENSIONS,
  EXTENSION_CONTENT_TYPE,
  MAX_FILE_SIZE_BYTES,
  getExtension,
  putToS3,
  validateFile,
} from "../lib/uploads";

const API_URL = import.meta.env.VITE_API_URL as string;
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string;

type FileEntry = {
  file: File;
  status: "pending" | "uploading" | "done" | "error";
  progress: number;
  key?: string;
  error?: string;
};

type FormState = "idle" | "submitting" | "success" | "error";

// `as const` (not `Record<Variant, ...>`) so indexed access by Variant returns
// the precise union of value types instead of `T | undefined` under
// noUncheckedIndexedAccess.
const STYLES = {
  light: {
    label: "block text-sm font-medium text-slate-200",
    input:
      "mt-1 block w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100 focus:border-slate-500 focus:outline-none disabled:opacity-50",
    textarea:
      "mt-1 block w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100 placeholder-slate-400 focus:border-slate-500 focus:outline-none disabled:opacity-50",
    dropZoneBase:
      "mt-1 cursor-pointer rounded-md border-2 border-dashed p-6 text-center backdrop-blur-[8px] transition-colors",
    dropZoneIdle: "border-slate-700 hover:border-slate-600",
    dropZoneActive: "border-slate-400 bg-slate-800/50",
    dropZoneHint: "text-sm text-slate-300",
    fileRow:
      "flex items-center gap-3 rounded-md border border-slate-800 bg-slate-900/50 px-3 py-2 text-sm backdrop-blur-[8px]",
    fileName: "flex-1 truncate text-slate-200",
    fileMeta: "text-xs text-slate-400",
    fileProgress: "text-xs text-sky-400",
    fileDone: "text-xs text-emerald-400",
    fileRemove: "text-xs text-slate-400 hover:text-slate-200",
    errorAlert:
      "rounded-md border border-red-700/50 bg-red-900/20 px-3 py-2 text-sm text-red-300 backdrop-blur-[8px]",
    submit:
      "w-full rounded-md bg-slate-100 px-4 py-2 font-semibold text-slate-900 transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-60",
    successCard:
      "rounded-lg border border-emerald-700/50 bg-emerald-900/20 p-6 text-center backdrop-blur-[8px]",
  },
  dark: {
    label: "block text-sm font-medium text-slate-200",
    input:
      "mt-1 block w-full rounded-md border border-bg-border bg-bg-base px-3 py-2 text-slate-100 focus:border-brand-500 focus:outline-none disabled:opacity-50",
    textarea:
      "mt-1 block w-full rounded-md border border-bg-border bg-bg-base px-3 py-2 text-slate-100 placeholder-slate-400 focus:border-brand-500 focus:outline-none disabled:opacity-50",
    dropZoneBase:
      "mt-1 cursor-pointer rounded-md border-2 border-dashed p-6 text-center backdrop-blur-[8px] transition-colors",
    dropZoneIdle: "border-bg-border hover:border-brand-400",
    dropZoneActive: "border-brand-400 bg-brand-500/10",
    dropZoneHint: "text-sm text-slate-300",
    fileRow:
      "flex items-center gap-3 rounded-md border border-bg-border bg-bg-base/60 px-3 py-2 text-sm backdrop-blur-[8px]",
    fileName: "flex-1 truncate text-slate-200",
    fileMeta: "text-xs text-slate-400",
    fileProgress: "text-xs text-brand-400",
    fileDone: "text-xs text-emerald-400",
    fileRemove: "text-xs text-slate-400 hover:text-slate-200",
    errorAlert:
      "rounded-md border border-red-700/50 bg-red-900/20 px-3 py-2 text-sm text-red-300 backdrop-blur-[8px]",
    submit:
      "w-full rounded-md bg-brand-500 px-4 py-2 font-semibold text-bg-base transition-colors hover:bg-brand-400 disabled:cursor-not-allowed disabled:opacity-60",
    successCard:
      "rounded-lg border border-emerald-700/50 bg-emerald-900/20 p-6 text-center backdrop-blur-[8px]",
  },
} as const;

export type ContactFormVariant = keyof typeof STYLES;

export interface ContactFormProps {
  variant?: ContactFormVariant;
}

export function ContactForm({ variant = "light" }: ContactFormProps = {}) {
  const s = STYLES[variant];

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [honeypot, setHoneypot] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");

  const [formState, setFormState] = useState<FormState>("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [submissionId, setSubmissionId] = useState<string>("");

  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  // --- Turnstile widget setup -------------------------------------------
  // The script in index.html loads asynchronously, so window.turnstile may
  // not exist on first render. We poll briefly until it's ready, then call
  // render() once. The callback fires when the widget produces a token.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current) return;
    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      if (!window.turnstile) {
        setTimeout(tryRender, 50);
        return;
      }
      if (widgetIdRef.current) return; // already rendered
      widgetIdRef.current = window.turnstile.render(turnstileRef.current!, {
        sitekey: TURNSTILE_SITE_KEY,
        // WHY size:flexible + appearance:interaction-only: Cloudflare
        // deprecated size:"invisible". The replacement is to keep the
        // widget visually flexible and tell it to only appear when a real
        // challenge is needed. Legit traffic sees nothing; bots see the
        // challenge UI.
        size: "flexible",
        appearance: "interaction-only",
        callback: (token) => setTurnstileToken(token),
        "error-callback": () => setTurnstileToken(""),
        "expired-callback": () => setTurnstileToken(""),
      });
    };
    tryRender();
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  // --- File handling ----------------------------------------------------
  const addFiles = (incoming: FileList | File[]) => {
    const next: FileEntry[] = [];
    for (const f of Array.from(incoming)) {
      const err = validateFile(f);
      if (err) {
        setErrorMessage(
          err.kind === "extension"
            ? `"${f.name}" — extension ${err.ext || "(none)"} is not allowed`
            : `"${f.name}" — file is ${(err.size / 1024 / 1024).toFixed(1)} MB, max is 50 MB`
        );
        continue;
      }
      next.push({ file: f, status: "pending", progress: 0 });
    }
    if (next.length) setFiles((prev) => [...prev, ...next]);
  };

  const removeFile = (idx: number) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  // --- Submit handler ---------------------------------------------------
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");

    if (!name.trim() || !email.trim() || !message.trim()) {
      setErrorMessage("Name, email, and message are required.");
      return;
    }
    if (!turnstileToken) {
      setErrorMessage("Still verifying you're human — try again in a moment.");
      return;
    }

    setFormState("submitting");

    try {
      const uploadedKeys: string[] = [];
      // Sequential uploads. Could parallelize, but most users attach 1–3
      // files and parallel uploads make progress UI confusing.
      for (let i = 0; i < files.length; i++) {
        const entry = files[i];
        if (!entry || entry.status === "done") {
          if (entry?.key) uploadedKeys.push(entry.key);
          continue;
        }

        const ext = getExtension(entry.file.name);
        const contentType = EXTENSION_CONTENT_TYPE[ext];
        if (!contentType) throw new Error(`unsupported extension: ${ext}`);

        // Step 1: presign
        const presignRes = await fetch(`${API_URL}/presign`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ filename: entry.file.name, contentType }),
        });
        if (!presignRes.ok) throw new Error(`presign failed: ${await presignRes.text()}`);
        const { url, key } = (await presignRes.json()) as { url: string; key: string };

        // Step 2: PUT to S3 with progress
        setFiles((prev) => prev.map((p, idx) => (idx === i ? { ...p, status: "uploading", key } : p)));
        await putToS3(url, entry.file, (pct) => {
          setFiles((prev) => prev.map((p, idx) => (idx === i ? { ...p, progress: pct } : p)));
        });
        setFiles((prev) => prev.map((p, idx) => (idx === i ? { ...p, status: "done", progress: 100 } : p)));
        uploadedKeys.push(key);
      }

      // Step 3: submit metadata
      const submitRes = await fetch(`${API_URL}/submit`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          company,
          phone,
          message,
          fileKeys: uploadedKeys,
          honeypot,
          turnstileToken,
        }),
      });
      if (!submitRes.ok) throw new Error(`submit failed: ${await submitRes.text()}`);
      const { submissionId } = (await submitRes.json()) as { submissionId: string };
      setSubmissionId(submissionId);
      setFormState("success");
    } catch (err) {
      console.error(err);
      setErrorMessage(err instanceof Error ? err.message : String(err));
      setFormState("error");
      // Reset the Turnstile token so the user gets a fresh one for the retry.
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
        setTurnstileToken("");
      }
    }
  };

  // --- Render -----------------------------------------------------------
  if (formState === "success") {
    return (
      <div className={s.successCard}>
        <h3 className="text-lg font-semibold text-emerald-300">Got it — thanks.</h3>
        <p className="mt-1 text-sm text-slate-300">
          We'll be in touch shortly. Reference: <code className="text-slate-200">{submissionId}</code>
        </p>
      </div>
    );
  }

  const isSubmitting = formState === "submitting";

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {/* Honeypot. WHY hidden via CSS, not type="hidden": bots that auto-
          fill forms typically skip type="hidden" fields but happily fill
          anything visible in the DOM. CSS-hiding (offscreen + zero opacity
          + no tab-stop) makes it invisible to humans and irresistible to bots. */}
      <input
        type="text"
        name="company_url"
        tabIndex={-1}
        autoComplete="off"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        className="absolute left-[-9999px] h-0 w-0 opacity-0"
        aria-hidden="true"
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Name" value={name} onChange={setName} required disabled={isSubmitting} variant={variant} />
        <Field label="Email" type="email" value={email} onChange={setEmail} required disabled={isSubmitting} variant={variant} />
        <Field label="Company" value={company} onChange={setCompany} disabled={isSubmitting} variant={variant} />
        <Field label="Phone" value={phone} onChange={setPhone} disabled={isSubmitting} variant={variant} />
      </div>

      <div className="relative">
        <textarea
          required
          aria-required="true"
          disabled={isSubmitting}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          placeholder="Tell us what you need quoted — quantities, materials, tolerances, due date."
          aria-label="Message"
          className={`${s.textarea} pr-7`}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute right-2.5 top-3 text-xs text-red-400"
        >
          *
        </span>
      </div>

      <div>
        <h3 className={s.label}>Drawings &amp; files</h3>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
          }}
          onClick={() => fileInputRef.current?.click()}
          className={`${s.dropZoneBase} ${isDragging ? s.dropZoneActive : s.dropZoneIdle}`}
        >
          <p className={s.dropZoneHint}>
            Drag & drop, or click to choose. STEP / STP / DXF / DWG / PDF / PNG / JPG, up to 50 MB each.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ALLOWED_EXTENSIONS.join(",")}
            disabled={isSubmitting}
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = ""; // allow selecting the same file again after removal
            }}
            className="hidden"
          />
        </div>

        {files.length > 0 && (
          <ul className="mt-3 space-y-2">
            {files.map((entry, idx) => (
              <li key={idx} className={s.fileRow}>
                <span className={s.fileName}>{entry.file.name}</span>
                <span className={s.fileMeta}>{(entry.file.size / 1024 / 1024).toFixed(2)} MB</span>
                {entry.status === "uploading" && (
                  <span className={s.fileProgress}>{entry.progress}%</span>
                )}
                {entry.status === "done" && <span className={s.fileDone}>✓</span>}
                {!isSubmitting && (
                  <button
                    type="button"
                    onClick={() => removeFile(idx)}
                    className={s.fileRemove}
                    aria-label={`Remove ${entry.file.name}`}
                  >
                    ✕
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Invisible Turnstile widget — Cloudflare renders nothing visible
          here in invisible mode, but the div must exist as the mount point. */}
      <div ref={turnstileRef} />

      {errorMessage && (
        <p className={s.errorAlert}>{errorMessage}</p>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className={s.submit}
      >
        {isSubmitting ? "Sending…" : "Request a quote"}
      </button>
    </form>
  );
}

function Field({
  label,
  type = "text",
  value,
  onChange,
  required,
  disabled,
  variant,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  disabled?: boolean;
  variant: ContactFormVariant;
}) {
  const s = STYLES[variant];
  return (
    <div className="relative">
      <input
        type={type}
        required={required}
        aria-required={required || undefined}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={label}
        aria-label={label}
        className={`${s.input}${required ? " pr-7" : ""}`}
      />
      {required && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-xs text-red-400"
        >
          *
        </span>
      )}
    </div>
  );
}
